import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import GlobalAudioControls from '../../components/ui/GlobalAudioControls';
import ConnectionStatusIndicator from '../../components/ui/ConnectionStatusIndicator';
import QuickActionFloatingMenu from '../../components/ui/QuickActionFloatingMenu';
import UserCard from './components/UserCard';
import FilterPanel from './components/FilterPanel';
import MatchNotification from './components/MatchNotification';
import EmptyState from './components/EmptyState';
import LoadingState from './components/LoadingState';

const MeetSomeoneNew = () => {
  const [users, setUsers] = useState([]);
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [matchNotification, setMatchNotification] = useState(null);
  const [playingAudio, setPlayingAudio] = useState({});
  const [dragState, setDragState] = useState({ isDragging: false, startX: 0, currentX: 0 });
  const cardRef = useRef(null);

  const [filters, setFilters] = useState({
    ageRange: [22, 35],
    distance: 25,
    compatibilityMin: 60,
    zodiacSigns: [],
    musicalPreferences: [],
    onlineOnly: false
  });

  // Mock user data
  const mockUsers = [
    {
      id: 1,
      name: "Luna Starweaver",
      age: 28,
      location: "San Francisco, CA",
      distance: "3 miles away",
      profileImage: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=600&fit=crop&crop=face",
      compatibilityScore: 87,
      astrology: {
        sun: { sign: "Pisces", symbol: "♓" },
        moon: { sign: "Cancer", symbol: "♋" },
        rising: { sign: "Scorpio", symbol: "♏" }
      },
      currentTone: {
        key: "D♭",
        mode: "Lydian",
        description: "Mystical and ethereal, with deep emotional resonance"
      },
      harmonicAspects: [
        { type: "trine", angle: 120 },
        { type: "sextile", angle: 60 },
        { type: "conjunction", angle: 0 }
      ],
      musicalCompatibility: 92,
      harmonicKey: "F# Major",
      isOnline: true
    },
    {
      id: 2,
      name: "Orion Nightsong",
      age: 32,
      location: "Oakland, CA",
      distance: "8 miles away",
      profileImage: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=600&fit=crop&crop=face",
      compatibilityScore: 73,
      astrology: {
        sun: { sign: "Leo", symbol: "♌" },
        moon: { sign: "Gemini", symbol: "♊" },
        rising: { sign: "Aquarius", symbol: "♒" }
      },
      currentTone: {
        key: "A",
        mode: "Dorian",
        description: "Bold and expressive with innovative harmonies"
      },
      harmonicAspects: [
        { type: "square", angle: 90 },
        { type: "trine", angle: 240 },
        { type: "sextile", angle: 300 }
      ],
      musicalCompatibility: 78,
      harmonicKey: "C Major",
      isOnline: false
    },
    {
      id: 3,
      name: "Celeste Moonbeam",
      age: 26,
      location: "Berkeley, CA",
      distance: "12 miles away",
      profileImage: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=600&fit=crop&crop=face",
      compatibilityScore: 91,
      astrology: {
        sun: { sign: "Virgo", symbol: "♍" },
        moon: { sign: "Taurus", symbol: "♉" },
        rising: { sign: "Capricorn", symbol: "♑" }
      },
      currentTone: {
        key: "E♭",
        mode: "Mixolydian",
        description: "Grounded and harmonious with earthy undertones"
      },
      harmonicAspects: [
        { type: "trine", angle: 120 },
        { type: "trine", angle: 240 },
        { type: "sextile", angle: 60 }
      ],
      musicalCompatibility: 89,
      harmonicKey: "G Minor",
      isOnline: true
    },
    {
      id: 4,
      name: "Phoenix Stardust",
      age: 29,
      location: "San Jose, CA",
      distance: "18 miles away",
      profileImage: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=600&fit=crop&crop=face",
      compatibilityScore: 65,
      astrology: {
        sun: { sign: "Aries", symbol: "♈" },
        moon: { sign: "Sagittarius", symbol: "♐" },
        rising: { sign: "Leo", symbol: "♌" }
      },
      currentTone: {
        key: "B",
        mode: "Phrygian",
        description: "Fiery and passionate with dynamic energy"
      },
      harmonicAspects: [
        { type: "square", angle: 90 },
        { type: "opposition", angle: 180 },
        { type: "conjunction", angle: 0 }
      ],
      musicalCompatibility: 71,
      harmonicKey: "D Major",
      isOnline: true
    }
  ];

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setUsers(mockUsers);
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleSwipe = (userId, direction) => {
    const user = users.find(u => u.id === userId);
    
    if (direction === 'right') {
      // Simulate match probability
      const isMatch = Math.random() > 0.6;
      if (isMatch) {
        setMatchNotification(user);
      }
    }

    // Move to next user
    setCurrentUserIndex(prev => prev + 1);
  };

  const handleAudioToggle = (userId, isPlaying) => {
    setPlayingAudio(prev => ({
      ...prev,
      [userId]: isPlaying
    }));
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    // In a real app, this would trigger a new user search
    console.log('Filters updated:', newFilters);
  };

  const handleMatchClose = () => {
    setMatchNotification(null);
  };

  const handleStartChat = (match) => {
    console.log('Starting chat with:', match.name);
    // Navigate to chat interface
  };

  const handleViewProfile = (match) => {
    console.log('Viewing profile of:', match.name);
    // Navigate to profile view
  };

  const handleMouseDown = (e) => {
    setDragState({
      isDragging: true,
      startX: e.clientX,
      currentX: e.clientX
    });
  };

  const handleMouseMove = (e) => {
    if (!dragState.isDragging) return;
    
    setDragState(prev => ({
      ...prev,
      currentX: e.clientX
    }));
  };

  const handleMouseUp = () => {
    if (!dragState.isDragging) return;
    
    const deltaX = dragState.currentX - dragState.startX;
    const threshold = 100;
    
    if (Math.abs(deltaX) > threshold) {
      const direction = deltaX > 0 ? 'right' : 'left';
      const currentUser = users[currentUserIndex];
      if (currentUser) {
        handleSwipe(currentUser.id, direction);
      }
    }
    
    setDragState({ isDragging: false, startX: 0, currentX: 0 });
  };

  const currentUser = users[currentUserIndex];
  const hasMoreUsers = currentUserIndex < users.length;

  const getCardStyle = () => {
    if (!dragState.isDragging) return {};
    
    const deltaX = dragState.currentX - dragState.startX;
    const rotation = deltaX * 0.1;
    
    return {
      transform: `translateX(${deltaX}px) rotate(${rotation}deg)`,
      opacity: 1 - Math.abs(deltaX) / 300
    };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Meet Someone New - AstroTone</title>
          <meta name="description" content="Discover astrological-musical compatibility with other souls through our innovative swipe-based interface." />
        </Helmet>
        
        <Header />
        <main className="pt-16 pb-20">
          <LoadingState message="Discovering cosmic connections..." />
        </main>
        <GlobalAudioControls />
        <QuickActionFloatingMenu />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Meet Someone New - AstroTone</title>
        <meta name="description" content="Discover astrological-musical compatibility with other souls through our innovative swipe-based interface." />
      </Helmet>
      
      <Header />
      
      <main className="pt-16 pb-20">
        {/* Page Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-bold text-text-primary">
                Meet Someone New
              </h1>
              <p className="text-text-secondary mt-1">
                Discover souls through astrological harmony
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <ConnectionStatusIndicator />
              <Button
                variant="ghost"
                iconName="Filter"
                onClick={() => setShowFilters(true)}
                className="relative"
              >
                {(filters.zodiacSigns?.length > 0 || filters.musicalPreferences?.length > 0) && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full"></div>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!hasMoreUsers ? (
            <EmptyState 
              type="no-users" 
              onAction={() => setShowFilters(true)} 
            />
          ) : (
            <div className="flex justify-center">
              <div 
                className="relative w-full max-w-sm"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {/* Stack of cards for depth */}
                {users.slice(currentUserIndex, currentUserIndex + 3).map((user, index) => (
                  <div
                    key={user.id}
                    className="absolute inset-0"
                    style={{
                      zIndex: 10 - index,
                      transform: `scale(${1 - index * 0.05}) translateY(${index * 8}px)`,
                      opacity: 1 - index * 0.3
                    }}
                  >
                    <UserCard
                      user={user}
                      onSwipe={handleSwipe}
                      onAudioToggle={handleAudioToggle}
                      isPlaying={playingAudio[user.id] || false}
                      style={index === 0 ? getCardStyle() : {}}
                      isDragging={index === 0 && dragState.isDragging}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Swipe Instructions */}
          {hasMoreUsers && (
            <div className="mt-8 text-center">
              <div className="flex items-center justify-center space-x-8 text-text-secondary">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-error/20 rounded-full flex items-center justify-center">
                    <Icon name="ArrowLeft" size={16} color="#EF4444" />
                  </div>
                  <span className="text-sm">Swipe left to pass</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-success/20 rounded-full flex items-center justify-center">
                    <Icon name="ArrowRight" size={16} color="#10B981" />
                  </div>
                  <span className="text-sm">Swipe right to connect</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Filter Panel */}
      <FilterPanel
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      {/* Match Notification */}
      <MatchNotification
        match={matchNotification}
        onClose={handleMatchClose}
        onStartChat={handleStartChat}
        onViewProfile={handleViewProfile}
      />

      <GlobalAudioControls />
      <QuickActionFloatingMenu />
    </div>
  );
};

export default MeetSomeoneNew;